import { urlUtil } from '@cmg/common';
import {
  ActionPanel,
  ActionPanelContent,
  ActionPanelContentWrapper,
  ActionPanelHeader,
  ActionPanelMenu,
  Typography,
} from '@cmg/design-system';
import React from 'react';
import { useLocation } from 'react-router-dom';

import { useIsSmallScreen } from '../../common/hooks/useIsSmallScreen';
import { useCmgEntityKeyContext } from './contexts/CmgEntityKeyContext';
import {
  OfferingSidePanelMatchProvider,
  OfferingSidePanelSearchProvider,
} from './hooks/useOfferingSidePanelMatch';
import { MobileActionPanelMenu } from './MobileActionPanelMenu';
import OfferingSidePanel from './OfferingSidePanel';
import { getSidePanelMatchUrl } from './OfferingSidePanel.model';
import { OfferingSidePanelContent } from './OfferingSidePanelContent';
import { OfferingSidePanelContextProvider } from './order-book/indication-activity/context-old/OfferingSidePanelContext';
import { useCloseSidePanel } from './utils/useCloseSidePanel';
// we cache the original match.url and use it internally as the prefixRoutePath for
// all internal side-panel routes
export const OfferingSidePanelRouteContext = React.createContext('');
export const OfferingSidePanelContainer: React.FC = () => {
  const { sidePanelMatch, closeSidePanel: handleOnClose } = useCloseSidePanel();

  const offeringId = sidePanelMatch?.offeringId;
  const { cmgEntityKey: cmgEntityKeyFromUrlQueryParam } = useCmgEntityKeyContext();
  const { search } = useLocation();
  const indicationId = urlUtil.queryParse(search)?.indicationId as string;
  const isOpen = !!sidePanelMatch;

  const isSmallScreen = useIsSmallScreen();

  return (
    <ActionPanel
      variant="persistent"
      open={isOpen}
      onClose={handleOnClose}
      dismissible
      data-testid="side-panel"
    >
      {isOpen && (
        <OfferingSidePanelMatchProvider value={sidePanelMatch}>
          <OfferingSidePanelSearchProvider value={search}>
            <OfferingSidePanelRouteContext.Provider
              value={getSidePanelMatchUrl(sidePanelMatch?.url)}
            >
              <OfferingSidePanelContextProvider
                offeringId={offeringId!}
                cmgEntityKey={cmgEntityKeyFromUrlQueryParam}
                indicationId={indicationId}
              >
                {!isSmallScreen && (
                  <ActionPanelMenu>
                    <OfferingSidePanelContent
                      offeringId={sidePanelMatch.offeringId}
                      baseSidePanelUrl={getSidePanelMatchUrl(sidePanelMatch?.url)}
                    />
                  </ActionPanelMenu>
                )}

                <ActionPanelContentWrapper width={{ xs: '100%', sm: 600, md: 650, lg: 720 }}>
                  <ActionPanelHeader>
                    {isSmallScreen ? (
                      <MobileActionPanelMenu
                        offeringId={offeringId!}
                        baseSidePanelUrl={getSidePanelMatchUrl(sidePanelMatch?.url)}
                      />
                    ) : (
                      <Typography variant="h2" component="h2" id="offering-side-panel-header" />
                    )}
                  </ActionPanelHeader>
                  <ActionPanelContent>
                    <OfferingSidePanel />
                  </ActionPanelContent>
                </ActionPanelContentWrapper>
              </OfferingSidePanelContextProvider>
            </OfferingSidePanelRouteContext.Provider>
          </OfferingSidePanelSearchProvider>
        </OfferingSidePanelMatchProvider>
      )}
    </ActionPanel>
  );
};
